/**
 * BUCHART COLBERT — Gift With Purchase (GWP)
 * ─────────────────────────────────────────────────────────────
 * Tier structure (evaluated at checkout, not during cart build):
 *   $75   – $185  → 1 sample
 *   $185.01 – $299.99 → 2 samples
 *   $300+          → 3 samples
 *
 * Samples fetched from the "GWP - Samples" collection via AJAX.
 * No Storefront API token required.
 *
 * Cart watcher: polls /cart.js every 3 s. If qualifying subtotal
 * (non-GWP items) drops below the tier that earned the current
 * GWP count, excess or all GWP items are removed automatically.
 * ─────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  // ─── Configuration ────────────────────────────────────────
  const GWP_CONFIG = {
    collectionHandle: 'gwp-samples',  // URL handle of your GWP samples collection
    tiers: [
      { min: 75,     max: 185.00,   slots: 1 },
      { min: 185.01, max: 299.99,   slots: 2 },
      { min: 300,    max: Infinity, slots: 3 },
    ],
    checkoutButtonSelectors: [
      'button[name="checkout"]',
      'input[name="checkout"]',
      'a[href="/checkout"]',
      '[data-checkout-button]',
      '.cart__checkout-button',
      '#checkout',
      'button.shopify-payment-button__button',
    ],
    // How often (ms) to poll the cart for threshold violations.
    // 3 000 ms is a reasonable balance between responsiveness and
    // request volume; lower if you need near-instant removal.
    watchInterval: 3000,
  };

  // ─── State ────────────────────────────────────────────────
  const state = {
    open: false,
    samples: [],
    selected: new Set(),
    slots: 0,
    submitting: false,
    interceptedButton: null,
    watcherTimer: null,
  };

  // ─── Tier Resolution ──────────────────────────────────────
  /**
   * Returns the raw cart, enriched with a `qualifyingSubtotal`
   * field that excludes GWP line items so a customer cannot game
   * the threshold by relying on $0-priced samples.
   */
  async function fetchCart() {
    const res = await fetch('/cart.js');
    if (!res.ok) throw new Error('cart.js fetch failed');
    return res.json();
  }

  function resolveSlots(subtotalCents) {
    const subtotal = subtotalCents / 100;
    for (const tier of GWP_CONFIG.tiers) {
      if (subtotal >= tier.min && subtotal <= tier.max) return tier.slots;
    }
    return 0;
  }

  /**
   * Given a raw Shopify cart object, returns:
   *   qualifyingSubtotalCents — sum of non-GWP line prices
   *   gwpItems                — array of GWP line-item objects
   */
  function analyseCart(cart) {
    const gwpItems = cart.items.filter(
      (item) => item.properties && item.properties._gwp === 'true'
    );
    const gwpTotal = gwpItems.reduce(
      (sum, item) => sum + item.final_line_price, 0
    );
    const qualifyingSubtotalCents = cart.total_price - gwpTotal;
    return { qualifyingSubtotalCents, gwpItems };
  }

  // Legacy helper kept for callers that only need the subtotal dollar value
  async function fetchCartSubtotal() {
    try {
      const cart = await fetchCart();
      const { qualifyingSubtotalCents } = analyseCart(cart);
      return qualifyingSubtotalCents / 100;
    } catch {
      return 0;
    }
  }

  // ─── Fetch samples via collection AJAX endpoint ───────────
  async function fetchSamples() {
    try {
      const res = await fetch(
        `/collections/${GWP_CONFIG.collectionHandle}/products.json?limit=20`
      );
      if (!res.ok) throw new Error(`Collection fetch failed: ${res.status}`);
      const data = await res.json();
      const products = data.products || [];
      return products.map((p) => {
        const variant = p.variants[0];
        const image = p.images?.[0]?.src || null;
        const note = p.body_html
          ? new DOMParser().parseFromString(p.body_html, 'text/html').body.textContent.split('.')[0].trim()
          : '';
        return {
          variantId: variant.id,
          title: p.title.replace(/\s*\d+\s*mL\b/i, '').trim(),
          note,
          image,
        };
      });
    } catch (err) {
      console.error('[GWP] Failed to fetch samples:', err);
      return [];
    }
  }

  // ─── Cart operations ──────────────────────────────────────
  async function addSamplesToCart(variantIds) {
    const items = variantIds.map((id) => ({
      id,
      quantity: 1,
      properties: { _gwp: 'true', _gwp_label: 'Complimentary Sample' },
    }));
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) throw new Error('Cart add failed');
    return res.json();
  }

  async function clearExistingGWP() {
    const cart = await fetchCart();
    const { gwpItems } = analyseCart(cart);
    if (!gwpItems.length) return;
    await removeGWPItems(gwpItems);
  }

  /**
   * Removes a specific array of GWP line items from the cart.
   */
  async function removeGWPItems(gwpItems) {
    if (!gwpItems.length) return;
    const updates = {};
    gwpItems.forEach((item) => { updates[item.key] = 0; });
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });
  }

  // ─── Cart Watcher ─────────────────────────────────────────
  /**
   * Polls the cart periodically. On every tick:
   *   1. Compute the qualifying subtotal (non-GWP items only).
   *   2. Resolve how many GWP slots that subtotal earns.
   *   3. If the cart holds MORE GWP items than the customer now
   *      qualifies for, remove the excess (or all, if 0 slots).
   *
   * This catches the case where a customer:
   *   • adds qualifying items → proceeds to checkout → selects samples
   *   • returns to the cart and removes the qualifying items
   *   • attempts to check out with free samples and no qualifying spend
   */
  async function cartWatcherTick() {
    try {
      const cart = await fetchCart();
      const { qualifyingSubtotalCents, gwpItems } = analyseCart(cart);
      const eligibleSlots = resolveSlots(qualifyingSubtotalCents);
      const currentGWPCount = gwpItems.length;

      if (currentGWPCount === 0) return; // nothing to do

      if (eligibleSlots === 0) {
        // Cart no longer qualifies at all — remove every GWP item
        console.info('[GWP] Threshold no longer met. Removing all GWP items.');
        await removeGWPItems(gwpItems);
        notifyGWPRemoved('all');
        return;
      }

      if (currentGWPCount > eligibleSlots) {
        // Downgraded tier — remove the excess items (last-added first)
        const excess = gwpItems.slice(eligibleSlots); // keep first N, remove rest
        console.info(`[GWP] Tier downgraded. Removing ${excess.length} excess GWP item(s).`);
        await removeGWPItems(excess);
        notifyGWPRemoved('partial', eligibleSlots);
      }
    } catch (err) {
      // Silent — don't spam the console on transient network errors
    }
  }

  /**
   * Starts the cart polling loop. Safe to call multiple times —
   * clears any existing timer first.
   */
  function startCartWatcher() {
    if (state.watcherTimer) clearInterval(state.watcherTimer);
    state.watcherTimer = setInterval(cartWatcherTick, GWP_CONFIG.watchInterval);
  }

  /**
   * When the cart is fully empty (all GWP removed, no qualifying items),
   * shows a centered overlay message and redirects to the home page.
   * When only partially downgraded, shows a brief toast.
   */
  function notifyGWPRemoved(scope, remainingSlots) {
    if (scope === 'all') {
      showEmptyCartOverlay();
    } else {
      showToast(`Your samples have been adjusted to match your current order (${remainingSlots} sample${remainingSlots !== 1 ? 's' : ''}).`);
    }
  }

  function showEmptyCartOverlay() {
    document.getElementById('gwp-empty-overlay')?.remove();

    const overlay = document.createElement('div');
    overlay.id = 'gwp-empty-overlay';
    overlay.setAttribute('role', 'status');
    overlay.setAttribute('aria-live', 'polite');
    Object.assign(overlay.style, {
      position:       'fixed',
      inset:          '0',
      background:     '#ffffff',
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      justifyContent: 'center',
      gap:            '12px',
      zIndex:         '10001',
      opacity:        '0',
      transition:     'opacity 0.4s ease',
    });
    overlay.innerHTML = `
      <p style="
        font-family: 'Avenir Next', 'Avenir', -apple-system, BlinkMacSystemFont, sans-serif;
        font-size: 13px;
        font-weight: 400;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #0f0f0f;
        margin: 0;
        text-align: center;
      ">Your cart is empty</p>
      <p style="
        font-family: 'Avenir Next', 'Avenir', -apple-system, BlinkMacSystemFont, sans-serif;
        font-size: 11px;
        font-weight: 300;
        letter-spacing: 0.04em;
        color: rgba(15,15,15,0.45);
        margin: 0;
        text-align: center;
      ">Continuing to shop\u2026</p>
    `;
    document.body.appendChild(overlay);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => { overlay.style.opacity = '1'; });
    });

    setTimeout(() => {
      window.location.href = '/';
    }, 2200);
  }

  function showToast(message) {
    document.getElementById('gwp-toast')?.remove();

    const toast = document.createElement('div');
    toast.id = 'gwp-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    Object.assign(toast.style, {
      position:      'fixed',
      bottom:        '24px',
      left:          '50%',
      transform:     'translateX(-50%)',
      background:    '#0f0f0f',
      color:         '#ffffff',
      fontFamily:    "'Avenir Next', 'Avenir', -apple-system, BlinkMacSystemFont, sans-serif",
      fontSize:      '12px',
      fontWeight:    '400',
      letterSpacing: '0.03em',
      lineHeight:    '1.5',
      padding:       '12px 20px',
      maxWidth:      '340px',
      textAlign:     'center',
      zIndex:        '10000',
      opacity:       '0',
      transition:    'opacity 0.3s ease',
      pointerEvents: 'none',
    });
    toast.textContent = message;
    document.body.appendChild(toast);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => { toast.style.opacity = '1'; });
    });

    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 350);
    }, 5000);
  }

  // ─── Build Drawer DOM ────────────────────────────────────
  function buildDrawer() {
    if (document.getElementById('gwp-drawer')) return;

    const overlay = document.createElement('div');
    overlay.id = 'gwp-overlay';
    overlay.addEventListener('click', handleSkip);

    const drawer = document.createElement('div');
    drawer.id = 'gwp-drawer';
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('aria-label', 'Select your complimentary samples');
    drawer.innerHTML = `
      <div class="gwp-header">
        <p class="gwp-header-eyebrow">A gift from Buchart Colbert</p>
        <h2 class="gwp-header-title">Your complimentary samples</h2>
        <p class="gwp-header-subtitle">Choose what accompanies your order</p>
        <div class="gwp-tier-badge">
          <span class="gwp-tier-dot"></span>
          <span id="gwp-tier-label">Loading…</span>
        </div>
        <button class="gwp-close" id="gwp-close-btn" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      <div class="gwp-progress" id="gwp-progress" style="display:none">
        <span class="gwp-progress-label">Selected</span>
        <div class="gwp-progress-track">
          <div class="gwp-progress-fill" id="gwp-progress-fill" style="width:0%"></div>
        </div>
        <span class="gwp-progress-count" id="gwp-progress-count">0 / 0</span>
      </div>

      <div class="gwp-samples" id="gwp-samples-list">
        <div class="gwp-loading">
          <div class="gwp-loading-ring"></div>
          <span class="gwp-loading-text">Preparing your samples</span>
        </div>
      </div>

      <div class="gwp-footer">
        <button class="gwp-cta" id="gwp-confirm-btn" disabled>
          Confirm &amp; Proceed to Checkout
        </button>
        <button class="gwp-skip" id="gwp-skip-btn">
          Skip — proceed without samples
        </button>
      </div>
    `;

    document.body.appendChild(overlay);
    document.body.appendChild(drawer);

    document.getElementById('gwp-close-btn').addEventListener('click', handleSkip);
    document.getElementById('gwp-skip-btn').addEventListener('click', handleSkip);
    document.getElementById('gwp-confirm-btn').addEventListener('click', handleConfirm);
  }

  // ─── Render Samples ──────────────────────────────────────
  function renderSamples() {
    const list = document.getElementById('gwp-samples-list');
    const progress = document.getElementById('gwp-progress');

    if (!state.samples.length) {
      list.innerHTML = `<div class="gwp-error"><p>No samples available at this time.</p></div>`;
      progress.style.display = 'none';
      return;
    }

    progress.style.display = 'flex';
    updateProgress();

    list.innerHTML = state.samples.map((s) => {
      const imgEl = s.image
        ? `<img class="gwp-sample-img" src="${s.image}" alt="${escapeHtml(s.title)}" loading="lazy">`
        : `<div class="gwp-sample-img-placeholder">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </div>`;
      return `
        <div class="gwp-sample-card" data-variant-id="${s.variantId}" role="checkbox" aria-checked="false" tabindex="0">
          ${imgEl}
          <div class="gwp-sample-info">
            <p class="gwp-sample-name">${escapeHtml(s.title)}</p>
            ${s.note ? `<p class="gwp-sample-note">${escapeHtml(s.note)}</p>` : ''}
          </div>
          <div class="gwp-check" aria-hidden="true">
            <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="2,6 5,9 10,3"/>
            </svg>
          </div>
        </div>
      `;
    }).join('');

    list.querySelectorAll('.gwp-sample-card').forEach((card) => {
      card.addEventListener('click', () => toggleSample(card));
      card.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          toggleSample(card);
        }
      });
    });
  }

  function toggleSample(card) {
    const vid = parseInt(card.dataset.variantId, 10);
    if (state.selected.has(vid)) {
      state.selected.delete(vid);
      card.classList.remove('gwp-selected');
      card.setAttribute('aria-checked', 'false');
    } else {
      if (state.selected.size >= state.slots) return;
      state.selected.add(vid);
      card.classList.add('gwp-selected');
      card.setAttribute('aria-checked', 'true');
    }
    updateProgress();
    updateCardStates();
    updateConfirmButton();
  }

  function updateCardStates() {
    const atCap = state.selected.size >= state.slots;
    document.querySelectorAll('.gwp-sample-card').forEach((card) => {
      const vid = parseInt(card.dataset.variantId, 10);
      card.classList.toggle('gwp-disabled', atCap && !state.selected.has(vid));
    });
  }

  function updateProgress() {
    const fill = document.getElementById('gwp-progress-fill');
    const count = document.getElementById('gwp-progress-count');
    const pct = state.slots ? (state.selected.size / state.slots) * 100 : 0;
    if (fill) fill.style.width = `${pct}%`;
    if (count) count.textContent = `${state.selected.size} / ${state.slots}`;
  }

  function updateConfirmButton() {
    const btn = document.getElementById('gwp-confirm-btn');
    if (btn) btn.disabled = state.selected.size === 0;
  }

  // ─── Open / Close ────────────────────────────────────────
  async function openDrawer(subtotal) {
    buildDrawer();
    state.slots = resolveSlots(subtotal * 100); // resolveSlots now expects cents

    const tierLabel = document.getElementById('gwp-tier-label');
    if (tierLabel) {
      tierLabel.textContent =
        state.slots === 1 ? 'One complimentary sample'
        : state.slots === 2 ? 'Two complimentary samples'
        : 'Three complimentary samples';
    }

    requestAnimationFrame(() => {
      document.getElementById('gwp-overlay').classList.add('gwp-visible');
      document.getElementById('gwp-drawer').classList.add('gwp-open');
    });
    document.body.style.overflow = 'hidden';
    state.open = true;

    if (!state.samples.length) {
      try {
        state.samples = await fetchSamples();
      } catch {
        state.samples = [];
      }
    }

    renderSamples();
  }

  function closeDrawer() {
    document.getElementById('gwp-overlay')?.classList.remove('gwp-visible');
    document.getElementById('gwp-drawer')?.classList.remove('gwp-open');
    document.body.style.overflow = '';
    state.open = false;
  }

  // ─── Handlers ────────────────────────────────────────────
  async function handleSkip() {
    closeDrawer();
    await proceedToCheckout();
  }

  async function handleConfirm() {
    if (state.submitting || state.selected.size === 0) return;
    state.submitting = true;

    const btn = document.getElementById('gwp-confirm-btn');
    const originalText = btn.textContent;
    btn.textContent = 'Adding samples…';
    btn.classList.add('gwp-loading-btn');

    try {
      await clearExistingGWP();
      await addSamplesToCart([...state.selected]);
      closeDrawer();
      await proceedToCheckout();
    } catch (err) {
      console.error('[GWP] Failed to add samples:', err);
      btn.textContent = 'Something went wrong — try again';
      btn.classList.remove('gwp-loading-btn');
      state.submitting = false;
      setTimeout(() => { btn.textContent = originalText; }, 3000);
    }
  }

  async function proceedToCheckout() {
    state.interceptedButton = null;
    window.location.href = '/checkout';
  }

  // ─── Intercept Checkout Clicks ───────────────────────────
  function interceptCheckoutButtons() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest(GWP_CONFIG.checkoutButtonSelectors.join(', '));
      if (!btn) return;
      if (state.open) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      state.interceptedButton = btn;
      state.selected = new Set();
      state.submitting = false;

      const cart = await fetchCart();
      const { qualifyingSubtotalCents, gwpItems } = analyseCart(cart);
      const subtotal = qualifyingSubtotalCents / 100;
      const minThreshold = Math.min(...GWP_CONFIG.tiers.map(t => t.min));

      if (subtotal <= 0 || subtotal < minThreshold) {
        // Below all thresholds — clear any orphaned GWP items then go to checkout
        if (gwpItems.length) await removeGWPItems(gwpItems);
        await proceedToCheckout();
        return;
      }

      const eligibleSlots = resolveSlots(qualifyingSubtotalCents);
      const gwpCount = gwpItems.length;

      // More GWP items than the current tier allows — clear and reopen
      if (gwpCount > eligibleSlots) {
        await removeGWPItems(gwpItems);
        state.samples = [];
        await openDrawer(subtotal);
        return;
      }

      // Already has the right number of samples — go straight through
      if (gwpCount === eligibleSlots) {
        await proceedToCheckout();
        return;
      }

      // Qualifies for more samples than currently selected — show drawer
      await openDrawer(subtotal);
    }, true);
  }

  // ─── Utility ─────────────────────────────────────────────
  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ─── Init ────────────────────────────────────────────────
  function init() {
    fetchSamples()
      .then((samples) => { state.samples = samples; })
      .catch(() => {});
    interceptCheckoutButtons();
    startCartWatcher(); // ← continuously validates GWP entitlement
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();